import api from '../api.js';
import db from '../../versa/db.js';
import { loadOptions } from '../../versa/formset.js';

/**
 * Linha do modal de entrada: lista embalagens do produto e preenche fabricante (somente leitura).
 * Usar após types.observer(['company_id', 'product_id-0']).
 */
export default function ()
{
	const SHARED = { _this: null };

	return {

		/** Mesma linha da tabela: #product_packaging_id-N → #manufacturer_display-N (locate com id composto falha às vezes). */
		_manufacturerDisplayField()
		{
			const m = String(this.selector || '').match(/-(\d+)$/);
			const idx = m ? m[1] : '0';
			if (this.form?.getFieldBySelector)
			{
				const f = this.form.getFieldBySelector(`#manufacturer_display-${idx}`);
				if (f)
					return f;
			}
			return this.locate(this.makeId('manufacturer_display-0'));
		},

		/** Valor id da embalagem: internal, ou option do datalist quando o rótulo está na caixa. */
		_resolvePackagingIdFromUi(opts)
		{
			const node = document.querySelector(this.selector);
			let v = this.getValue();
			if (v != null && v !== '' && (opts || []).some((p) => String(p.id) === String(v)))
				return v;
			if (!node)
				return v;
			const typed = (node.value || '').trim();
			if (!typed || !node.list)
				return v;
			const opt = Array.from(node.list.options || []).find((o) =>
			{
				const t = (o.innerText || '').trim();
				return t === typed || String(o.value) === typed;
			});
			return opt != null ? opt.value : v;
		},

		async _applyManufacturerLabel(company_id, manufacturerId, mfrField)
		{
			if (!mfrField || !company_id)
				return;
			const mid = manufacturerId != null && manufacturerId !== '' ? Number(manufacturerId) : NaN;
			if (!Number.isFinite(mid) || mid < 1)
			{
				mfrField.setValue('');
				mfrField.refresh();
				return;
			}
			try
			{
				let m = await db.get('manufacturers', [company_id, mid]);
				if (!m?.nick && !m?.name)
				{
					m = await api.call(api.select_manufacturer, company_id, mid);
					if (m && typeof m === 'object')
						await db.set('manufacturers', [m]).catch(() => {});
				}
				mfrField.setValue(m ? (m.name || m.nick || '') : '');
				mfrField.refresh();
			}
			catch (_e)
			{
				mfrField.setValue('');
				mfrField.refresh();
			}
		},

		async _fillManufacturerFromProduct(company_id, product_id, mfrField)
		{
			if (!mfrField)
				return;
			if (!company_id || !product_id || Number(product_id) < 1)
			{
				mfrField.setValue('');
				mfrField.refresh();
				return;
			}
			try
			{
				const p = await db.get('products', [company_id, Number(product_id)]);
				await this._applyManufacturerLabel(company_id, p?.manufacturer_id, mfrField);
			}
			catch (_e)
			{
				mfrField.setValue('');
				mfrField.refresh();
			}
		},

		/** Fabricante da embalagem (GTIN); se a embalagem não tiver fabricante, usa o do produto (cadastro de embalagem). */
		async _syncMfrFromPackagingList(opts, packagingId, company_id, product_id, mfrField)
		{
			if (!mfrField)
				return;
			const ppid = packagingId != null && packagingId !== '' ? Number(packagingId) : NaN;
			if (!Number.isFinite(ppid) || ppid < 1)
			{
				mfrField.setValue('');
				mfrField.refresh();
				return;
			}
			const row = (opts || []).find((p) => Number(p.id) === ppid || String(p.id) === String(packagingId));
			const mid = row?.manufacturer_id;
			if (mid != null && mid !== '' && Number(mid) >= 1)
			{
				await this._applyManufacturerLabel(company_id, Number(mid), mfrField);
				return;
			}
			await this._fillManufacturerFromProduct(company_id, product_id, mfrField);
		},

		async actions(/* ev, publisher, value */)
		{
			const action = async () =>
			{
				const company_id = this.locate('company_id')?.getValue();
				const productField = this.locate(this.makeId('product_id-0'));
				const product_id = productField?.getValue();
				const preserve = this.getValue();
				const mfrField = this._manufacturerDisplayField();
				if (!company_id || !product_id || Number(product_id) < 1)
				{
					this._lastPackagingOpts = [];
					loadOptions(this.selector, [], 'id', ['code']);
					this.setValue('');
					this.refresh();
					if (mfrField)
					{
						mfrField.setValue('');
						mfrField.refresh();
					}
					return;
				}
				const list = await api.call(api.list_product_packagings_by_product, company_id, Number(product_id));
				const opts = Array.isArray(list) ? list : [];
				this._lastPackagingOpts = opts;
				loadOptions(this.selector, opts, 'id', ['code', 'level', 'factor'], ' · ');
				this.refresh();
				// Decidir id antes de limpar: preserve (edição) ou texto da caixa casando option (rótulo vs id).
				let candidate = this._resolvePackagingIdFromUi(opts);
				if (candidate == null || candidate === '')
					candidate = preserve;
				if (candidate == null || candidate === '')
					candidate = this.getValue();
				const stillValid = candidate != null && candidate !== '' && opts.some((p) => String(p.id) === String(candidate));
				if (!stillValid)
				{
					this.setValue('');
					if (mfrField)
					{
						mfrField.setValue('');
						mfrField.refresh();
					}
					return;
				}
				if (String(this.getValue() ?? '') !== String(candidate))
					this.setValue(candidate);
				this.refresh();
				await this._syncMfrFromPackagingList(opts, candidate, company_id, product_id, mfrField);
			};
			await this.preventMultiCalls(SHARED, action, () => this.refresh(), []);
		},

		captureEvents(_this = this)
		{
			this.safeProtoCall(_this);
			const node = document.querySelector(this.selector);
			if (!node)
				return;
			const onPick = () =>
			{
				const company_id = this.locate('company_id')?.getValue();
				const product_id = this.locate(this.makeId('product_id-0'))?.getValue();
				const mfrField = this._manufacturerDisplayField();
				const list = this._lastPackagingOpts || [];
				const raw = this._resolvePackagingIdFromUi(list);
				void this._syncMfrFromPackagingList(list, raw, company_id, product_id, mfrField);
			};
			node.addEventListener('blur', onPick);
			node.addEventListener('change', onPick);
		},

	};

}
